import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { motion } from "framer-motion";
import { MagnifyingGlass, Download, X, Clock, Trash, PencilSimple, Check } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const ACTIVITY_TYPES = [
  { key: "", label: "All types" },
  { key: "call", label: "Calls" },
  { key: "contact", label: "Contacts" },
  { key: "appt_set_indiv", label: "Appointments Set" },
  { key: "appts_set_week", label: "Appts On The Books" },
  { key: "appts_seen_week", label: "Seen On The Books" },
  { key: "referral_received", label: "Referrals Received" },
  { key: "networking_event", label: "Networking Events Attended" },
  { key: "workout", label: "Workouts" },
  { key: "case_submitted", label: "Cases Submitted" },
  { key: "case_paid", label: "Cases Paid" },
  { key: "submitted_premium", label: "Submitted Premium" },
  { key: "paid_premium", label: "Paid Premium" },
  { key: "gdc_logged", label: "Annuity/Investment Commission" },
];

const PAGE_SIZE = 50;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const prettyType = (t) => ACTIVITY_TYPES.find((x) => x.key === t)?.label || t.replace(/_/g, " ");

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ActivityHistory() {
  const { user } = useAuth();
  const isMpOrAdmin = user?.role === "managing_partner" || user?.role === "admin";
  const isFd = user?.role === "field_director";

  // Filters
  const [q, setQ] = useState("");
  const [activityType, setActivityType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [scope, setScope] = useState(isMpOrAdmin || isFd ? "mine" : "mine");
  const [offset, setOffset] = useState(0);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const datesValid = (!start || !end) || (new Date(start) <= new Date(end));

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (activityType) p.set("activity_type", activityType);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (scope) p.set("scope", scope);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(offset));
    return p.toString();
  }, [q, activityType, start, end, scope, offset]);

  const load = useCallback(async () => {
    if (!datesValid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/activities?${queryString}`);
      // Endpoint returns envelope when history params are used
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load history");
    } finally {
      setLoading(false);
    }
  }, [queryString, datesValid]);

  useEffect(() => { load(); }, [load]);

  // Reset offset when filters change (queryString carries them — only reset if offset>0)
  useEffect(() => {
    setOffset(0);
  }, [q, activityType, start, end, scope]);

  const clearFilters = () => {
    setQ("");
    setActivityType("");
    setStart("");
    setEnd("");
    setOffset(0);
  };

  const downloadCsv = async () => {
    try {
      // Strip pagination params; CSV exports all matching rows.
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (activityType) p.set("activity_type", activityType);
      if (start) p.set("start", start);
      if (end) p.set("end", end);
      if (scope) p.set("scope", scope);
      const res = await api.get(`/activities/export.csv?${p.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cfn-activity-history-${todayIso()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete this ${prettyType(a.activity_type)} entry${a.client_name ? ` for ${a.client_name}` : ""}? This removes its points.`)) return;
    try {
      await api.delete(`/activities/${a.id}`);
      toast.success("Entry deleted");
      load();
    } catch {
      toast.error("Could not delete (auto-credited entries from Cases can only be removed by changing the case status).");
    }
  };

  // Caller can edit/delete this entry?
  // - Admin: always
  // - Owner agent: their own
  // - MP / FD: any entry in their downline
  // Auto-credited Case entries are skipped (toggled via a.case_id below).
  const canModify = (a) => {
    if (user?.role === "admin") return true;
    if (a.agent_id === user?.id) return true;
    if (["managing_partner", "field_director"].includes(user?.role) && a.agent_name) return true;
    return false;
  };

  const openEdit = (a) => {
    setEditing(a);
    setEditForm({
      count: a.count ?? 1,
      premium_amount: a.premium_amount ?? "",
      client_name: a.client_name || "",
      notes: a.notes || "",
      product_line: a.product_line || "",
      logged_date: (a.created_at || "").slice(0, 10),
      source: a.source || "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const payload = {};
      if (editForm.count !== undefined && editForm.count !== "") payload.count = Math.max(1, parseInt(editForm.count, 10) || 1);
      if (editForm.premium_amount !== "" && editForm.premium_amount !== null) payload.premium_amount = Number(editForm.premium_amount);
      if (editForm.client_name !== undefined) payload.client_name = editForm.client_name;
      if (editForm.notes !== undefined) payload.notes = editForm.notes;
      if (editForm.product_line) payload.product_line = editForm.product_line;
      if (editForm.source !== undefined && editForm.source !== (editing.source || "")) {
        payload.source = editForm.source || null;
      }
      if (editForm.logged_date && editForm.logged_date !== (editing.created_at || "").slice(0, 10)) {
        payload.logged_date = editForm.logged_date;
      }
      await api.patch(`/activities/${editing.id}`, payload);
      toast.success("Entry updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    } finally {
      setSavingEdit(false);
    }
  };

  const hasFilters = q || activityType || start || end;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + items.length, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Activity</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1 flex items-center gap-3">
            History <Clock weight="fill" className="text-[#2563eb]" />
          </h1>
          <p className="text-zinc-600 mt-2">Search and review every activity you&apos;ve logged — by client, product, type, or date range.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(isMpOrAdmin || isFd) && (
            <div className="brut bg-white p-1.5 inline-flex" data-testid="history-scope-toggle">
              <button onClick={() => setScope("mine")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "mine" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="history-scope-mine">Mine</button>
              {isMpOrAdmin && user?.role === "managing_partner" && (
                <button onClick={() => setScope("team")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "team" ? "bg-[#2563eb] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="history-scope-team">My Team</button>
              )}
              {isMpOrAdmin && (
                <button onClick={() => setScope("firm")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "firm" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="history-scope-firm">{user?.role === "admin" ? "Firm" : "All"}</button>
              )}
            </div>
          )}
          <Button
            onClick={downloadCsv}
            disabled={total === 0}
            className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#2563eb] hover:shadow-[6px_6px_0_0_#2563eb] font-bold uppercase tracking-widest text-xs h-10"
            data-testid="history-csv-btn"
          >
            <Download size={14} weight="bold" className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* Filters card */}
      <div className="brut bg-white p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="history-filters">
        <div className="md:col-span-5">
          <Label className="font-bold uppercase tracking-widest text-[10px]">Search client, product or notes</Label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Robert Smith, IUL, Annuity…"
              className="pl-9 border-2 border-black rounded-none h-10 focus-visible:ring-0 focus:shadow-[3px_3px_0_0_#09090b]"
              data-testid="history-search-input"
            />
          </div>
        </div>
        <div className="md:col-span-3">
          <Label className="font-bold uppercase tracking-widest text-[10px]">Type</Label>
          <select
            value={activityType}
            onChange={(e) => setActivityType(e.target.value)}
            className="mt-1 w-full h-10 border-2 border-black px-3 font-medium bg-white"
            data-testid="history-type-select"
          >
            {ACTIVITY_TYPES.map((t) => <option key={t.key || "all"} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="font-bold uppercase tracking-widest text-[10px]">From</Label>
          <input
            type="date"
            value={start}
            max={end || todayIso()}
            onChange={(e) => setStart(e.target.value)}
            className="mt-1 w-full border-2 border-black h-10 px-3 font-medium bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#09090b]"
            data-testid="history-start"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="font-bold uppercase tracking-widest text-[10px]">To</Label>
          <input
            type="date"
            value={end}
            min={start || undefined}
            max={todayIso()}
            onChange={(e) => setEnd(e.target.value)}
            className="mt-1 w-full border-2 border-black h-10 px-3 font-medium bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#09090b]"
            data-testid="history-end"
          />
        </div>

        {hasFilters && (
          <div className="md:col-span-12 flex items-center gap-2">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-[#ef4444]"
              data-testid="history-clear-filters"
            >
              <X size={12} weight="bold" /> Clear filters
            </button>
            {!datesValid && (
              <span className="text-xs font-bold uppercase tracking-widest text-[#ef4444]">End must be on or after start</span>
            )}
          </div>
        )}
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between" data-testid="history-meta">
        <div className="text-sm text-zinc-600">
          {loading ? "Loading…" : (
            total === 0
              ? "No entries match these filters."
              : <>Showing <span className="font-bold">{showingFrom}–{showingTo}</span> of <span className="font-bold">{total}</span> {total === 1 ? "entry" : "entries"}.</>
          )}
        </div>
        {total > PAGE_SIZE && (
          <div className="inline-flex gap-2">
            <Button
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={offset === 0}
              className="rounded-none bg-white text-black border-2 border-black h-8 px-3 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100 disabled:opacity-40"
              data-testid="history-prev"
            >Prev</Button>
            <Button
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={offset + items.length >= total}
              className="rounded-none bg-[#09090b] text-white border-2 border-black h-8 px-3 text-xs font-bold uppercase tracking-widest hover:bg-black disabled:opacity-40"
              data-testid="history-next"
            >Next</Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="brut bg-white overflow-x-auto">
        <table className="w-full border-collapse" data-testid="history-table">
          <thead>
            <tr className="bg-[#09090b] text-white">
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Date</th>
              {scope !== "mine" && (isMpOrAdmin || isFd) && (
                <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Agent</th>
              )}
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Type</th>
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Client</th>
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Product</th>
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Source</th>
              <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Carrier</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Count</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Premium</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Points</th>
              <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a, i) => (
              <motion.tr
                key={a.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: Math.min(i * 0.01, 0.3) }}
                className="border-b-2 border-black last:border-b-0 hover:bg-[#f4f4f5]"
                data-testid={`history-row-${a.id}`}
              >
                <td className="px-3 py-2 text-xs whitespace-nowrap">{(a.created_at || "").slice(0, 10)}</td>
                {scope !== "mine" && (isMpOrAdmin || isFd) && (
                  <td className="px-3 py-2 text-zinc-700">{a.agent_name}</td>
                )}
                <td className="px-3 py-2">
                  <span className="inline-flex items-center px-2 py-0.5 border-2 border-black text-[10px] font-black uppercase tracking-widest bg-[#dbeafe]">
                    {prettyType(a.activity_type)}
                  </span>
                </td>
                <td className="px-3 py-2 font-bold">{a.client_name || <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-zinc-700">{a.product_type || <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2">
                  {a.source ? (
                    <span
                      className="inline-block border-2 border-black px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest"
                      style={{
                        background:
                          a.source === "natural_market" ? "#a7f3d0" :
                          a.source === "referral" ? "#fde68a" :
                          a.source === "paid_lead" ? "#bae6fd" :
                          a.source === "cold_lead" ? "#c7d2fe" :
                          a.source === "networking" ? "#fed7aa" :
                          "#e4e4e7",
                      }}
                      data-testid={`history-source-${a.id}`}
                    >
                      {a.source === "natural_market" ? "Nat. Mkt"
                        : a.source === "referral" ? "Referral"
                        : a.source === "paid_lead" ? "Paid Lead"
                        : a.source === "cold_lead" ? "Cold Lead"
                        : a.source === "networking" ? "Networking"
                        : a.source}
                    </span>
                  ) : <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-3 py-2 text-zinc-700">{a.carrier_name || <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-right">{a.count || <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-right">{a.premium_amount ? fmt$(a.premium_amount) : <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-right font-display font-black">+{a.points || 0}</td>
                <td className="px-3 py-2 text-right">
                  {canModify(a) && !a.case_id && (
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => openEdit(a)}
                        className="border-2 border-black p-1.5 hover:bg-[#2563eb] hover:text-white"
                        data-testid={`history-edit-${a.id}`}
                        title="Edit entry"
                      >
                        <PencilSimple size={12} weight="bold" />
                      </button>
                      <button
                        onClick={() => del(a)}
                        className="border-2 border-black p-1.5 hover:bg-[#fb7185] hover:text-white"
                        data-testid={`history-delete-${a.id}`}
                        title="Delete entry"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  )}
                  {a.case_id && (
                    <span className="text-[10px] uppercase tracking-widest text-zinc-400" title="Auto-credit from a Case — change the case status to remove">Auto</span>
                  )}
                </td>
              </motion.tr>
            ))}
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={scope !== "mine" && (isMpOrAdmin || isFd) ? 11 : 10} className="px-4 py-12 text-center text-zinc-500">
                  Nothing here yet. {hasFilters ? "Adjust your filters or" : "Head to"} <span className="font-bold">Log Activity</span> to add entries.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit entry dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight">
              Edit {editing ? prettyType(editing.activity_type) : ""}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4" data-testid="history-edit-dialog">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-bold uppercase tracking-widest text-[10px]">Count</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editForm.count}
                    onChange={(e) => setEditForm({ ...editForm, count: e.target.value })}
                    className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0"
                    data-testid="history-edit-count"
                  />
                </div>
                <div>
                  <Label className="font-bold uppercase tracking-widest text-[10px]">Premium ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.premium_amount}
                    onChange={(e) => setEditForm({ ...editForm, premium_amount: e.target.value })}
                    placeholder="0"
                    className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0"
                    data-testid="history-edit-premium"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-bold uppercase tracking-widest text-[10px]">Client</Label>
                  <Input
                    value={editForm.client_name}
                    onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                    className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0"
                    data-testid="history-edit-client"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-bold uppercase tracking-widest text-[10px]">Source</Label>
                  <select
                    value={editForm.source || ""}
                    onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                    className="mt-1 w-full h-10 border-2 border-black px-3 bg-white font-medium"
                    data-testid="history-edit-source"
                  >
                    <option value="">— None —</option>
                    <option value="natural_market">Natural Market</option>
                    <option value="referral">Referral</option>
                    <option value="paid_lead">Paid Lead</option>
                    <option value="cold_lead">Cold Lead</option>
                    <option value="networking">Networking</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="font-bold uppercase tracking-widest text-[10px]">Date (Eastern)</Label>
                  <Input
                    type="date"
                    value={editForm.logged_date || ""}
                    onChange={(e) => setEditForm({ ...editForm, logged_date: e.target.value })}
                    max={new Date().toISOString().slice(0, 10)}
                    className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0"
                    data-testid="history-edit-date"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-bold uppercase tracking-widest text-[10px]">Notes</Label>
                  <Input
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0"
                    data-testid="history-edit-notes"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setEditing(null)}
                  className="rounded-none bg-white text-black border-2 border-black hover:bg-zinc-100 font-bold uppercase tracking-widest text-xs h-10"
                  data-testid="history-edit-cancel"
                >
                  <X size={14} weight="bold" className="mr-1" /> Cancel
                </Button>
                <Button
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="rounded-none bg-[#2563eb] text-white border-2 border-black shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 font-bold uppercase tracking-widest text-xs h-10"
                  data-testid="history-edit-save"
                >
                  <Check size={14} weight="bold" className="mr-1" /> {savingEdit ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
